import { FC, Fragment, type RefObject, useEffect, useRef, useState } from 'react';

import { LogoutButton } from '@eneco/components-next/auth';
import { useLinkComponent } from '@eneco/link';
import { NavLink, Box, Stack, Divider, Text, TransitionSlide, Stretch } from '@eneco/sparky';
import { ChevronLeftIcon, ChevronRightIcon } from '@eneco/sparky/icons';
import { getIllustration } from '@sitecore/common';
import { ProfileMenuRendering, Item as ProfileMenuRenderingItem } from '@sitecore/types/manual/NavigationBar';

import { ProfileArea } from './LoginMenu/ProfileArea';
import usePlaceholderComponentContent from './usePlaceholderComponentContent';

interface Props {
  setIsCloseButtonVisible?: (isVisible: boolean) => void;
}

type LinkComponent = ReturnType<typeof useLinkComponent>;
type ProfileMenuFields = ProfileMenuRendering['fields'];

interface MainMenuProps {
  fields: ProfileMenuFields;
  menuItems: ProfileMenuRenderingItem[];
  profileMenuRef: RefObject<HTMLDivElement | null>;
  onSubMenuClick: (item: ProfileMenuRenderingItem) => void;
  Link: LinkComponent;
}

const MainMenu: FC<MainMenuProps> = ({ fields, menuItems, profileMenuRef, onSubMenuClick, Link }) => (
  <>
    <div ref={profileMenuRef}>
      <ProfileArea label={fields.customerNumberLabel.value} />
      <Divider />
    </div>
    {menuItems.length > 0 && (
      <Box padding="6">
        <Stack gap="6" as="ul">
          {menuItems.map(item => {
            const { text, href, linktype } = item.fields.link.value;
            const isSubMenu = item.fields?.items?.length;
            const itemContent = isSubMenu ? (
              <li>
                <Stretch width={true}>
                  <NavLink
                    onClick={() => onSubMenuClick(item)}
                    rightIcon={<ChevronRightIcon />}
                    justifyContent="spaceBetween">
                    {text}
                  </NavLink>
                </Stretch>
              </li>
            ) : (
              <li>
                <Stretch width={true}>
                  <Link href={href} linkType={linktype}>
                    <NavLink>{text}</NavLink>
                  </Link>
                </Stretch>
              </li>
            );
            return (
              <Fragment key={item.id}>
                {itemContent} <Divider />
              </Fragment>
            );
          })}
        </Stack>
      </Box>
    )}
    <Box padding="6">
      <Stack gap="6" as="ul">
        <li>
          <LogoutButton label={fields.logoutLink.value.text || 'Log out'} />
        </li>
      </Stack>
    </Box>
  </>
);

interface SecondaryMenuProps {
  activeSecondaryMenu: ProfileMenuRenderingItem;
  isSecondaryMenuShown: boolean;
  closeSecondaryMenu: () => void;
  setIsSecondaryMenuShown: (isShown: boolean) => void;
  Link: LinkComponent;
}

const SecondaryMenu: FC<SecondaryMenuProps> = ({
  activeSecondaryMenu,
  isSecondaryMenuShown,
  closeSecondaryMenu,
  setIsSecondaryMenuShown,
  Link,
}) => (
  <TransitionSlide isEntering={isSecondaryMenuShown} isAbsolute={true} onLeaveFinished={closeSecondaryMenu}>
    <Box paddingX="5" paddingY="4" backgroundColor="backgroundPrimary">
      <Box paddingY="6">
        <Stretch width={true}>
          <NavLink
            isCurrent={true}
            onClick={() => setIsSecondaryMenuShown(false)}
            leftIcon={<ChevronLeftIcon color="iconBrand" />}>
            <Box paddingLeft="7">
              <Text weight="bold" color="textBrand">
                {activeSecondaryMenu?.fields?.link.value.text}
              </Text>
            </Box>
          </NavLink>
        </Stretch>
      </Box>
      <Divider />
      <Box paddingY="6">
        <Stack gap="6" as="ul">
          {activeSecondaryMenu?.fields?.items
            ?.filter(item => item.fields?.link?.value?.text)
            .map(item => {
              const { text, title, href, linktype } = item.fields.link.value;
              const { illustration } = item.fields;
              const Illustration = getIllustration(illustration?.value);
              return (
                <li key={item.id}>
                  <Stretch width={true}>
                    <Link href={href} linkType={linktype}>
                      <NavLink>
                        <Stack direction="row" alignY="center">
                          {Illustration && (
                            <Box paddingRight="5">
                              <Illustration size="small" />
                            </Box>
                          )}
                          <Stack>
                            <Text weight="bold">{text}</Text>
                            <Text weight="regular" size="BodyS" color="textLowEmphasis">
                              {title}
                            </Text>
                          </Stack>
                        </Stack>
                      </NavLink>
                    </Link>
                  </Stretch>
                </li>
              );
            })}
        </Stack>
      </Box>
    </Box>
  </TransitionSlide>
);

const ProfileMenu: FC<Props> = ({ setIsCloseButtonVisible }) => {
  const [activeSecondaryMenu, setActiveSecondaryMenu] = useState(null as ProfileMenuRenderingItem | null);
  const [isSecondaryMenuShown, setIsSecondaryMenuShown] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (activeSecondaryMenu && profileMenuRef.current) {
      profileMenuRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeSecondaryMenu]);

  const closeSecondaryMenu = () => {
    setActiveSecondaryMenu(null);
    setIsSecondaryMenuShown(false);
  };

  const Link = useLinkComponent();
  const content = usePlaceholderComponentContent<ProfileMenuRendering>('jss-side-navigation', 'ProfileMenu');
  useEffect(() => {
    setIsCloseButtonVisible?.(!activeSecondaryMenu);
  }, [activeSecondaryMenu, setIsCloseButtonVisible]);

  if (!content) return null;

  const { fields } = content;
  const menuItems = fields?.items?.filter(item => item.fields?.link?.value?.text);

  const handleSubMenuClick = (item: ProfileMenuRenderingItem) => {
    setIsSecondaryMenuShown(true);
    setActiveSecondaryMenu(item);
  };

  return (
    <>
      <MainMenu
        fields={fields}
        menuItems={menuItems}
        profileMenuRef={profileMenuRef}
        onSubMenuClick={handleSubMenuClick}
        Link={Link}
      />
      {activeSecondaryMenu && (
        <SecondaryMenu
          activeSecondaryMenu={activeSecondaryMenu}
          isSecondaryMenuShown={isSecondaryMenuShown}
          closeSecondaryMenu={closeSecondaryMenu}
          setIsSecondaryMenuShown={setIsSecondaryMenuShown}
          Link={Link}
        />
      )}
    </>
  );
};

export default ProfileMenu;
